import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { errMsg } from "@/lib/api";
import { CATEGORIES, BUY_UNITS, eurUnit, dateFr, unitLabel, catLabel } from "@/lib/format";
import PriceHistoryChart from "@/components/ingredients/PriceHistoryChart";
import { t } from "@/i18n";

const EMPTY = { name: "", category: "Autre", unit: "kg", current_unit_cost: "" };

export default function IngredientDialog({ open, ingredient, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(ingredient ? { name: ingredient.name, category: ingredient.category, unit: ingredient.unit, current_unit_cost: ingredient.current_unit_cost } : EMPTY);
  }, [open, ingredient]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...form, current_unit_cost: parseFloat(String(form.current_unit_cost).replace(",", ".")) || 0 };
    try {
      if (ingredient) await api.put(`/ingredients/${ingredient.id}`, body);
      else await api.post("/ingredients", body);
      toast.success(t("ingredientDialog.ingredient_enregistre"));
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const units = BUY_UNITS.includes(form.unit) ? BUY_UNITS : [...BUY_UNITS, form.unit];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="ingredient-dialog">
        <DialogHeader><DialogTitle>{ingredient ? t("ingredientDialog.modifier_l_ingredient") : t("common.nouvel_ingredient")}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-2">
            <Label>{t("common.nom")}</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ingredient-name-input" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-3 space-y-2 sm:col-span-1">
              <Label>{t("common.categorie")}</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="ingredient-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("ingredientDialog.unite_d_achat")}</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger data-testid="ingredient-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>{units.map((u) => <SelectItem key={u} value={u}>{unitLabel(u)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("ingredientDialog.prix_ht")}{unitLabel(form.unit)})</Label>
              <Input required inputMode="decimal" value={form.current_unit_cost} onChange={(e) => setForm({ ...form, current_unit_cost: e.target.value })} data-testid="ingredient-cost-input" />
            </div>
          </div>
          {ingredient?.price_history?.length > 0 && (
            <div className="space-y-4">
              <PriceHistoryChart history={ingredient.price_history} unit={unitLabel(ingredient.unit)} />
              <p className="eyebrow mb-2">{t("ingredientDialog.historique_des_prix")}</p>
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border p-2" data-testid="ingredient-price-history">
                {[...ingredient.price_history].reverse().map((h, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{dateFr(h.date)} · {h.supplier || "—"}</span>
                    <span className="num">{eurUnit(h.unit_cost)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t("common.annuler")}</Button>
            <Button type="submit" disabled={busy} data-testid="ingredient-save-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("common.enregistrer")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
